import { memo, useCallback, useState, useEffect } from "react";
import Table from "../../components/Table";
import { getOrdersHistory, getOrder, getOrderedAnimals } from "../../api/index.js";
import useListStyles from "../../components/styles/List.js";
import { useSnackbar } from "../../utils/index.js";
import Spinner from "../../components/Spinner.js";
import {
    orderNumberHeader,
    orderSellerHeader,
    orderBuyerHeader,
    orderCityHeader,
    orderDateHeader,
    executionDateHeader,
} from "../../components/TableHeaders.js";
import { Grid2, Box, Typography, FormControl, InputLabel, Select, MenuItem, TextField, Button } from "@mui/material";
import OrderDetailsCard from "../../components/OrderDetailsCard.js";
import { executeOrder } from "../../api/index.js";
import OrderedAnimalsPdf from "./OrderedAnimalsPdf.js";
import ArrowBack from "@mui/icons-material/ArrowBack";

const OrdersHistory = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [pendingOrders, setPendingOrders] = useState([]);
    const [executedOrders, setExecutedOrders] = useState([]);
    const [filteredPendingOrders, setFilteredPendingOrders] = useState([]);
    const [filteredExecutedOrders, setFilteredExecutedOrders] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [orderNumber, setOrderNumber] = useState("");
    const [step, setStep] = useState(1);
    const [searchedOrder, setSearchedOrder] = useState(null);
    const [fetchedOrderedAnimals, setFetchedOrderedAnimals] = useState([]);

    const classes = useListStyles();
    const { error } = useSnackbar();

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const { orders } = await getOrdersHistory();
            setPendingOrders(orders.filter((order) => !order.executed));
            setExecutedOrders(orders.filter((order) => order.executed));
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    }, [error]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const filterByYear = (orders) =>
            orders.filter(
                (order) =>
                    new Date(order.orderDate).getFullYear() === Number(selectedYear)
            );

        setFilteredPendingOrders(filterByYear(pendingOrders));
        setFilteredExecutedOrders(filterByYear(executedOrders));
    }, [selectedYear, pendingOrders, executedOrders]);

    const handleYearChange = (event) => {
        setSelectedYear(event.target.value);
    };

    const handleOrderSearch = async () => {
        if (orderNumber) {
            const { success: scs, order, message } = await getOrder(orderNumber);
            if (scs) {
                setSearchedOrder(order);
                setStep(2)
            } else {
                error(message)
            }
        }
    };

    const yearOptions = Array.from(
        { length: new Date().getFullYear() - 2000 + 1 },
        (_, i) => new Date().getFullYear() - i
    );

    const tableHeaders = [
        orderNumberHeader,
        orderSellerHeader,
        orderBuyerHeader,
        orderCityHeader,
        orderDateHeader,
        executionDateHeader,
    ];

    const onExecuteOrder = async (executeDate, orderNumber) => {
        setIsLoading(true)
        const { success: scs, message } = await executeOrder({ executeDate, orderNumber });
        if (scs) {
            fetchData();
            setStep(1);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    const orderedAnimalsGetSubmit = async (type) => {
        setIsLoading(true);
        const { success: scs, orderedAnimals, message } = await getOrderedAnimals(Number(selectedYear), type);
        if (scs) {
            setFetchedOrderedAnimals(orderedAnimals);
            setStep(3);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    return (
        <>
            <Spinner open={isLoading} />
            {step === 1 && (
                <Grid2 container className={classes.container} direction="column" sx={{ marginTop: "1rem", marginLeft: "3rem" }}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ width: "90%", marginBottom: "1rem" }}
                    >
                        <Box display="flex" alignItems="center" sx={{ gap: "1rem" }}>
                            <TextField
                                label="Αριθμός Παραγγελίας"
                                variant="outlined"
                                value={orderNumber}
                                onChange={(e) => setOrderNumber(e.target.value)}
                                sx={{ minWidth: "200px" }}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleOrderSearch}
                            >
                                Προβολή Παραγγελίας
                            </Button>
                        </Box>

                        <FormControl sx={{ minWidth: "150px" }}>
                            <InputLabel id="year-select-label">Έτος</InputLabel>
                            <Select
                                labelId="year-select-label"
                                value={selectedYear}
                                onChange={handleYearChange}
                            >
                                {yearOptions.map((year) => (
                                    <MenuItem key={year} value={year}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ width: "90%", marginBottom: "1rem", marginTop: "1rem" }}
                    >
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => orderedAnimalsGetSubmit("maleAnimal")}
                        >
                            Παραγγελθέντες Κριοί
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => orderedAnimalsGetSubmit("femaleAnimal")}
                        >
                            Παραγγελθείσες Προβατίνες
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => orderedAnimalsGetSubmit("lamb")}
                        >
                            Παραγγελθέντες Αμνοί
                        </Button>
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: "90%", marginBottom: "1rem" }}>
                        <Typography variant="h5" className={classes.form_title}>
                            Παραγγελίες Σε Εκκρεμότητα
                        </Typography>
                    </Box>
                    <Table
                        data={filteredPendingOrders}
                        noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν παραγγελίες σε εκκρεμότητα!"}
                        columns={tableHeaders.slice(0, -1)}
                    />

                    <div style={{ height: "50px" }} />

                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: "90%", marginBottom: "1rem" }}>
                        <Typography variant="h5" className={classes.form_title}>
                            Παραγγελίες που έχουν αποσταλεί
                        </Typography>
                    </Box>
                    <Table
                        data={filteredExecutedOrders}
                        noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν παραγγελίες που έχουν εκτελεστεί!"}
                        columns={tableHeaders}
                    />
                </Grid2>
            )}
            {step === 2 && (
                <>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        onClick={() => setStep(1)}
                        sx={{ marginBottom: "1rem" }}
                    >
                    </Button>
                    <OrderDetailsCard order={searchedOrder} onExecuteOrder={onExecuteOrder} orderNumber={orderNumber} />
                </>
            )}
            {step === 3 && (
                <>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        onClick={() => setStep(1)}
                        sx={{ marginBottom: "1rem" }}
                    >
                    </Button>
                    <OrderedAnimalsPdf orderedAnimals={fetchedOrderedAnimals} />
                </>
            )}

        </>
    );
};

export default memo(OrdersHistory);
